import React, { useState, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { FileUp, Loader2, FileText, Check } from 'lucide-react';
import { toast } from 'sonner';
import { importApi } from '@/lib/api';
import { usePW } from '@/lib/store';

const ACCEPT = '.docx,.pdf,.txt,.md,.markdown,.epub';
const MAX_BYTES = 20 * 1024 * 1024;

export function ImportManuscriptDialog({ open, onOpenChange }) {
  const { loadProject, refreshProjects } = usePW();
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [name, setName] = useState('');
  const [author, setAuthor] = useState('');
  const [wordsPerChapter, setWordsPerChapter] = useState(2500);
  // iter55f86 — When off, imported chapters are labelled "Chapter N"
  // instead of using the parser's guesses. Users can always rename
  // any chapter afterwards from the sidebar.
  const [chapterTitlesEnabled, setChapterTitlesEnabled] = useState(true);
  const inputRef = useRef(null);

  const pick = (f) => {
    setResult(null);
    if (!f) { setFile(null); return; }
    if (f.size > MAX_BYTES) {
      toast.error(`File too large (>${(MAX_BYTES / 1_000_000) | 0} MB).`);
      return;
    }
    setFile(f);
    // Pre-fill name from filename minus extension.
    if (!name) {
      const baseName = f.name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ');
      setName(baseName);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    pick(e.dataTransfer?.files?.[0]);
  };

  const submit = async () => {
    if (!file) { toast.warning('Pick a manuscript file first.'); return; }
    setBusy(true);
    try {
      const r = await importApi.manuscript(file, {
        name: name || undefined,
        author: author || undefined,
        words_per_chapter: wordsPerChapter,
        chapter_titles_enabled: chapterTitlesEnabled,
      });
      setResult(r);
      toast.success(`Imported ${r.imported.kept_chapters} chapter${r.imported.kept_chapters === 1 ? '' : 's'} (${r.imported.total_words.toLocaleString()} words).`);
      await refreshProjects();
      await loadProject(r.project.id, { silent: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Import failed.');
    } finally { setBusy(false); }
  };

  const finish = () => {
    setFile(null); setResult(null); setName(''); setAuthor('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="import-manuscript-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <FileUp className="h-4 w-4 text-primary" /> Import manuscript
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            DOCX, PDF, TXT, Markdown, or EPUB. The Loom will split your manuscript into chapters and create a new project.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <>
            <button
              onClick={() => inputRef.current?.click()}
              onDrop={onDrop}
              onDragOver={(e) => e.preventDefault()}
              data-testid="import-manuscript-drop"
              className={`mt-2 flex h-32 w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed bg-card/40 px-4 py-6 text-center transition-colors hover:border-primary hover:bg-primary/5 ${file ? 'border-primary text-foreground' : 'border-panel-border text-muted-foreground'}`}
            >
              {file ? (
                <>
                  <FileText className="h-5 w-5 text-primary" />
                  <div className="text-[12.5px] font-medium">{file.name}</div>
                  <div className="font-mono-stat text-[10.5px] text-muted-foreground">
                    {(file.size / 1024).toFixed(1)} KB · click to pick a different file
                  </div>
                </>
              ) : (
                <>
                  <FileUp className="h-5 w-5" />
                  <div className="text-[12px]">Drop a manuscript here, or click to browse</div>
                  <div className="text-[10.5px] text-muted-foreground">{ACCEPT}</div>
                </>
              )}
            </button>
            <input
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              data-testid="import-manuscript-input"
              className="hidden"
              onChange={(e) => pick(e.target.files?.[0])}
            />

            <div className="mt-4 grid gap-3 md:grid-cols-3">
              <div>
                <Label className="text-[11px]">Project name (optional)</Label>
                <Input
                  className="mt-1 h-8 text-[12px]"
                  placeholder="Auto-detected from manuscript"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="import-manuscript-name"
                />
              </div>
              <div>
                <Label className="text-[11px]">Author (optional)</Label>
                <Input
                  className="mt-1 h-8 text-[12px]"
                  placeholder="Auto-detected"
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  data-testid="import-manuscript-author"
                />
              </div>
              <div>
                <Label className="text-[11px]">Words per chapter</Label>
                <Input
                  className="mt-1 h-8 font-mono-stat text-[12px]"
                  type="number"
                  min={1000}
                  max={6000}
                  value={wordsPerChapter}
                  onChange={(e) => setWordsPerChapter(parseInt(e.target.value, 10) || 2500)}
                  data-testid="import-manuscript-wpc"
                />
              </div>
            </div>

            {/* iter55f86 — Chapter titles toggle. */}
            <label
              className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-panel-border/70 bg-secondary/30 p-2 text-[11.5px]"
              data-testid="import-manuscript-titles-row"
            >
              <input
                type="checkbox"
                className="mt-0.5 h-3.5 w-3.5"
                checked={chapterTitlesEnabled}
                onChange={(e) => setChapterTitlesEnabled(e.target.checked)}
                data-testid="import-manuscript-titles-toggle"
              />
              <span>
                <b className="font-medium text-foreground">Keep detected chapter titles</b> —
                <span className="text-muted-foreground">
                  {' '}When on, we&apos;ll use whatever titles the parser found in your manuscript.
                  Uncheck to number chapters plainly as <em>Chapter 1</em>, <em>Chapter 2</em>, etc.
                  You can always rename any chapter afterwards from the sidebar.
                </span>
              </span>
            </label>
          </>
        )}

        {result && (
          <div className="mt-3 rounded-md border border-success/40 bg-success/5 p-4 text-[12px]" data-testid="import-manuscript-result">
            <div className="mb-2 flex items-center gap-2 text-success">
              <Check className="h-4 w-4" />
              <span className="font-medium">Imported successfully.</span>
            </div>
            <ul className="space-y-1 text-muted-foreground">
              <li>Detected title: <b className="text-foreground">{result.imported.title_detected || '—'}</b></li>
              <li>Detected author: <b className="text-foreground">{result.imported.author_detected || '—'}</b></li>
              <li>Chapters detected: <b className="text-foreground">{result.imported.chapter_count}</b></li>
              <li>Chapters kept (capped at 20): <b className="text-foreground">{result.imported.kept_chapters}</b></li>
              <li>Total words: <b className="text-foreground">{result.imported.total_words.toLocaleString()}</b></li>
            </ul>
            <p className="mt-3 text-[11px] text-muted-foreground">
              Your new project is now active. You can refine variables, add characters, then run "Generate chapter" to expand or revise any chapter with AI.
            </p>
          </div>
        )}

        <DialogFooter className="mt-2">
          {result ? (
            <Button size="sm" onClick={finish} data-testid="import-manuscript-done">Done</Button>
          ) : (
            <>
              <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button
                size="sm"
                onClick={submit}
                disabled={busy || !file}
                data-testid="import-manuscript-submit"
                className="gap-1.5"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileUp className="h-3.5 w-3.5" />}
                {busy ? 'Importing…' : 'Import manuscript'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
